// printer.js — continuous-form printer paper path.
//
// The bridge supplies full-page 1bpp payloads (row-major, row_stride bytes/row,
// bit7 = leftmost pixel, 1 = dot fired). Each payload is the full accumulation
// of the CURRENT page so far. page_index 0xFFFFFFFF is a live snapshot of the
// in-progress page (requested ~2Hz while the head is active); a real page_index
// is a page that flushed (~5s after printing goes idle).
//
// Fan-fold paper emerges upward from the fixed print line. Its leading edge
// (local y = 0, the tear base) is anchored by:
//   surface translateY(native) = printLineNativeY − (currentLF − tearBaseLF)·PX_PER_LF
// Visible paper above the slot therefore equals the feed since the tear base.
//
// Page y_min/y_max values share the absolute g_bmp_y_steps coordinate. Pages
// are composited into bounded offscreen tiles at yMin - tearBase. Tearing exports
// the accumulated strip, clears the tiles, and rebases tearBase at the print line.
//
// Scrollback freezes the paper transform while detached; returning to the live
// print line resumes follow mode.
//
// Physical coordinate frame:
// Horizontal: PAPER_LEFT_STEPS=2435 (the LESB reference the head event's x_steps
// is already relative to) anchors frame x=0; 1px = 1 carriage half-step. The head
// shares this exact mapping (frac = head.x / PAPER_WIDTH_STEPS against the same
// printable box the page bitmap uses) so dot columns and the head stay aligned.
// Vertical: 1px = 2 LF steps. Both axes use a 180-DPI square grid before the
// complete paper surface is uniformly scaled to the viewport width.

const PAPER_BG = [0xfd, 0xfd, 0xfb]; // plain near-white continuous-form paper
const INK = [0x1b, 0x24, 0x30];      // ink dots
const SNAPSHOT_INDEX = 0xffffffff;

// ---- FIXED PHYSICAL COORDINATE FRAME (see header) -------------------------
const PAPER_LEFT_STEPS = 2435;   // raw carriage step at frame x=0 (LESB ref)
const PAPER_WIDTH_STEPS = 1320;  // fixed printable width (px @ SX=1)
const BMP_MARGIN = 2;            // PRINTER_BMP_MARGIN (bitmap px, 1/180")
// The C rasteriser is a 180-DPI SQUARE grid: x = carriage half-steps (1px =
// 1/180"), y = PIN ROWS = LF/2 (1px = 1/180"). So the roll's native px are 1/180"
// on both axes — horizontal 1px = 1 half-step, vertical 1px = 2 LF steps. This
// A 拷屏 LCD pixel therefore prints as a square 2×2 block.
const PX_PER_STEP = 1;           // native px per carriage half-step
const PX_PER_LF = 0.5;           // native px per LF step (= pin-row units, LF/2)

// ---- CONTINUOUS-FORM geometry (native px, 1/180" square grid) -------------
// Standard fan-fold has a ~1/2" tractor-hole strip down each edge. At 1px =
// 1/180", 1/2" = 90px and a 5/32" hole = 28px.
const STRIP_W_NATIVE = 90;       // sprocket-hole margin, each edge (native px, 1/2")
const TOTAL_W_NATIVE = PAPER_WIDTH_STEPS + 2 * STRIP_W_NATIVE; // full paper width
const HOLE_PITCH_PX = 90;        // 1/2" tractor pitch = 90 px @ 180 dpi
const HOLE_DIAM_PX = 28;         // ~5/32" hole diameter = 28 px @ 180 dpi

// ---- ROLL BUFFER + viewport ------------------------------------------------
const TILE_H = 1024;             // native rows per roll tile (created on demand)
const MAX_ROLL_ROWS = 20000;     // retention cap (native px ≈ 40k LF) — oldest dropped
const PRINT_LINE_PERCENT = 84;
const PRINT_LINE_FRAC = PRINT_LINE_PERCENT / 100; // slot: 0=window top, 1=bottom
// Length of the paper-body element below its leading edge. Only the part above
// the slot is ever visible (the apron hides the slot interior), so this just
// needs to exceed the retention window plus a viewport of slack.
const PAPER_BODY_LEN = MAX_ROLL_ROWS + 4096;
// Tractor drive-pin diameter (native px, 1/180"): the pins that engage the
// paper's punched holes — smaller than the 28px hole bore.
const PIN_DIAM_PX = 14;

export class PrinterPlaten {
  constructor(els, reduceMotion) {
    this.viewport = els.viewport;   // fixed window / paper well (overflow:hidden)
    this.scaler = els.scaler;       // native content paper, scaled to fit width
    this.surface = els.surface;     // translated vertically; paper + tiles + live
    this.overlay = els.overlay;     // carriage overlay (fixed print line + head)
    this.head = els.head;           // sliding carriage head element
    this.statusEl = els.status;     // small platen status line
    this.followBtn = els.followBtn; // 回到打印处 FOLLOW affordance (browse mode)
    this.sprockets = els.sprockets ||
      Array.from(this.viewport.querySelectorAll('.sprockets'));
    // Share the print-line anchor with the CSS apron, head, and knob.
    this.viewport.style.setProperty('--print-line', `${PRINT_LINE_PERCENT}%`);
    this.reduceMotion = reduceMotion;
    // Legacy #paper-empty placeholder: the no-paper initial state makes it
    // redundant (an empty platen IS the empty state), so hide it permanently and
    // never depend on its contents again.
    if (els.empty) els.empty.classList.add('is-hidden');

    // --- roll storage: absolute-LF tiles, keyed by local tile index ----------
    // localY(absLF) = absLF − tearBaseLF ; tile index = floor(localY / TILE_H).
    this._tiles = new Map();        // idx -> { canvas, ctx }
    // The paper's leading edge == the tear base. At power-on the LF accumulator
    // is 0 and no paper has been fed, so the edge starts at absolute LF 0 (any
    // BIOS init/homing feed then correctly emerges a sliver of leading edge).
    this._tearBaseLF = 0;           // absolute LF of the current tear line / edge
    this._contentTopLocal = null;   // min baked localY (for export bounds)
    this._contentBottomLocal = 0;   // max baked localY+height
    this._droppedBelowLocal = 0;    // localY below which tiles were retention-dropped

    // --- the paper itself: a white body whose top IS the leading edge --------
    // First child of the surface (beneath the content canvases), spanning the
    // full physical width including both punched-hole margins. Everything is in
    // native 1/180" units inside the scaled scaler, so the holes are laid out
    // ONCE at their true pitch and simply ride along with the paper.
    this._paperTopLocal = 0;        // leading-edge localY (≤0 if content margins poke up)
    this._paperDrag = null;
    this._paperBody = document.createElement('div');
    this._paperBody.className = 'paper-body';
    const pb = this._paperBody.style;
    pb.position = 'absolute';
    pb.left = (-STRIP_W_NATIVE) + 'px';
    pb.top = '0px';
    pb.width = TOTAL_W_NATIVE + 'px';
    pb.height = PAPER_BODY_LEN + 'px';
    pb.background = `rgb(${PAPER_BG.join(',')})`;
    // Leading-edge shadow so the emerging edge reads as a physical paper edge.
    pb.boxShadow = '0 -1px 0 rgba(0,0,0,0.35), 0 -3px 4px -2px rgba(0,0,0,0.25)';
    for (const side of ['left', 'right']) {
      const holes = document.createElement('div');
      holes.className = `paper-punch-strip paper-punch-${side}`;
      const hs = holes.style;
      hs.position = 'absolute';
      hs.top = '0';
      hs.bottom = '0';
      hs[side] = '0';
      hs.width = STRIP_W_NATIVE + 'px';
      const r = HOLE_DIAM_PX / 2;
      hs.backgroundImage = 'radial-gradient(circle at 50% 44%,' +
        ` rgba(12,15,20,0.55) 0 ${r * 0.60}px,` +
        ` rgba(12,15,20,0.30) ${r * 0.82}px,` +
        ` rgba(255,255,255,0.85) ${r}px,` +
        ` rgba(255,255,255,0) ${r + 2}px)`;
      hs.backgroundSize = `100% ${HOLE_PITCH_PX}px`;
      hs.backgroundRepeat = 'repeat-y';
      hs.backgroundPosition = `50% ${HOLE_PITCH_PX / 2}px`;
      holes.addEventListener('pointerdown', (e) => this._startPaperDrag(e));
      this._paperBody.appendChild(holes);
    }
    this.surface.appendChild(this._paperBody);

    // --- live (in-progress) page overlay -------------------------------------
    this._liveCanvas = document.createElement('canvas');
    this._liveCanvas.className = 'paper-live';
    this._liveCanvas.style.position = 'absolute';
    this._liveCanvas.style.display = 'none';
    // alpha:true so the paper renders TRANSPARENT and only ink is opaque — the
    // white paper body shows through, and overlapping pages MERGE their ink
    // (overprint) instead of the newer page's white erasing the older's dots.
    this._liveCtx = this._liveCanvas.getContext('2d', { alpha: true });
    this._liveCtx.imageSmoothingEnabled = false;
    this.surface.appendChild(this._liveCanvas); // last child == on top of tiles
    this._liveVisible = false;
    this._liveTopLocal = 0;
    this._liveBottomLocal = 0;
    this._liveLeftLocal = 0;
    this._liveFires = 0;

    // Scratch canvas used to rasterise a real page before baking onto tiles.
    // alpha:true (transparent paper) so baking a page that overlaps an already-
    // baked one ORs their ink on the tile rather than overwriting it (overprint).
    this._scratch = document.createElement('canvas');
    this._scratchCtx = this._scratch.getContext('2d', { alpha: true });
    this._scratchCtx.imageSmoothingEnabled = false;

    // --- vertical anchor / precedence bookkeeping ----------------------------
    this._currentLF = null;         // live LF accumulator (from head.y)
    this.hasContent = false;
    this._freshPaper = false;       // just torn: show 新纸 until the next content
    this._headWasActive = false;
    this._shownIndex = null;        // page_index of the current live overlay/bake
    this._shownFires = -1;
    this._maxRealIndex = -1;

    // --- follow / browse (scrollback) ----------------------------------------
    this._follow = true;            // true = auto-follow print line
    this._browseTy = 0;             // frozen surface translateY while browsing
    this._lastTyWritten = null;     // last surface translateY committed to the DOM
    this._animOn = null;            // cached transition state

    // --- scale (native paper -> window width) --------------------------------
    this._scale = 1;                // S = windowWidth / TOTAL_W_NATIVE
    this._printLineNativeY = 0;     // print-line Y in native (pre-scale) px
    this._recomputeScale();
    this._setTransition(true);
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this._recomputeScale());
      this._ro.observe(this.viewport);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => this._recomputeScale());
    }
    this._reseatBtn = document.createElement('button');
    this._reseatBtn.type = 'button';
    this._reseatBtn.className = 'paper-reseat is-hidden';
    this._reseatBtn.setAttribute('aria-label', 'Reseat paper');
    this._reseatBtn.title = 'Reseat paper to the position before this drag (R / Esc)';
    this._reseatBtn.innerHTML = '<b>重装纸</b>';
    this._reseatBtn.addEventListener('click', () => this._reseatDraggedPaper());
    this.viewport.appendChild(this._reseatBtn);
    this._onPaperDragKey = (e) => {
      if (!this._paperDrag) return;
      if (e.key !== 'r' && e.key !== 'R' && e.key !== 'Escape') return;
      e.preventDefault();
      this._reseatDraggedPaper();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this._onPaperDragKey, true);
    }
    // Wheel scrollback: passive:false ONLY here so we can preventDefault the
    // page scroll and drive the roll instead. Works anywhere over the paper well.
    this.viewport.addEventListener('wheel', (e) => this._onWheel(e), { passive: false });
    this.viewport.addEventListener('pointermove', (e) => this._movePaperDrag(e));
    this.viewport.addEventListener('pointerup', (e) => this._endPaperDrag(e));
    this.viewport.addEventListener('pointercancel', (e) => this._endPaperDrag(e));
    if (this.followBtn) this.followBtn.addEventListener('click', () => this._reengage());
  }

  bindMachine(machine) {
    this._machine = machine;
  }

  // ---- scale + tractor geometry --------------------------------------------
  // Fit the full native paper (content + both hole margins = TOTAL_W_NATIVE) to
  // the window and locate the print line in native units (so translateY, applied
  // inside the scaled scaler, is native px). The .sprockets strips are the
  // TRACTOR RAILS — mechanism, visible when no paper covers them — whose drive
  // pins are sized here at the physical pitch (they live in screen space, so they
  // need re-derivation on scale change; the paper's own holes are native-unit
  // children of the paper body and never need resizing).
  _recomputeScale() {
    const vw = this.viewport.clientWidth || 0;
    const vh = this.viewport.clientHeight || 0;
    const S = vw > 0 ? vw / TOTAL_W_NATIVE : 1;
    this._scale = S;
    const stripW = STRIP_W_NATIVE * S;
    this.scaler.style.left = stripW + 'px';
    this.scaler.style.transform = `scale(${S})`;
    this._printLineNativeY = S > 0 ? (PRINT_LINE_FRAC * vh) / S : 0;

    // Tractor drive pins at the paper-hole pitch. Same pitch + same vertical
    // tracking as the paper (see _applyTransform), so pins stay engaged with the
    // holes wherever paper covers the rail.
    const pitch = HOLE_PITCH_PX * S;
    const img = this._pinImage((PIN_DIAM_PX * S) / 2);
    for (const s of this.sprockets) {
      s.style.width = stripW + 'px';
      s.style.backgroundImage = img;
      s.style.backgroundSize = `100% ${pitch}px`;
      s.style.backgroundRepeat = 'repeat-y';
    }
    this._lastTyWritten = null; // S changed → force a re-write of transform/pins
    this._applyTransform();
  }

  // A single tractor drive pin: a small raised metal stud on the dark rail.
  _pinImage(r) {
    return 'radial-gradient(circle at 50% 46%,' +
      ` rgba(214,219,224,0.95) 0 ${(r * 0.45).toFixed(1)}px,` +
      ` rgba(150,157,164,0.85) ${(r * 0.8).toFixed(1)}px,` +
      ` rgba(60,66,72,0.9) ${r.toFixed(1)}px,` +
      ` rgba(0,0,0,0) ${(r + 1.2).toFixed(1)}px)`;
  }

  // Consume any pages the machine drained this frame, move the head, and re-anchor
  // the paper so the row being printed sits on the print line (in follow mode).
  render(machine, head) {
    this._machine = machine;   // kept so a physical tear can rebase the C knob clamp
    const pages = machine.drainPages();
    if (pages.length) {
      const reals = [];
      const snaps = [];
      for (const p of pages) {
        if (!p || !p.width || !p.height) continue;
        (p.index === SNAPSHOT_INDEX ? snaps : reals).push(p);
      }
      if (reals.length) {
        // A flushed page is authoritative and permanent: bake every one (in
        // index order) onto the roll and retire the live overlay for its epoch.
        reals.sort((a, b) => a.index - b.index);
        for (const p of reals) {
          this._bakePage(p);
          if (p.index > this._maxRealIndex) this._maxRealIndex = p.index;
        }
        this._hideLive();
        this._shownIndex = reals[reals.length - 1].index;
        this._shownFires = -1;
        this._markContent();
      } else if (snaps.length) {
        // Live snapshot: keep the fullest, and only if it advances the current
        // epoch (never regress; a snapshot after a real bake begins a fresh epoch
        // — the C side cleared the fires on flush).
        let best = null;
        for (const p of snaps) if (!best || p.fires >= best.fires) best = p;
        if (best && this._acceptSnapshot(best)) {
          this._showLive(best);
          this._shownIndex = SNAPSHOT_INDEX;
          this._shownFires = best.fires;
          this._markContent();
        }
      }
    }

    this._updateHead(head);
    this._updateAnchor(head);
    this._applyTransform();

    // When the head finishes a sweep (active -> idle edge), ask for one final
    // snapshot so the complete page shows immediately rather than trailing the
    // 2Hz preview cadence until the ~5s auto-flush lands.
    const active = !!(head && head.valid && (head.busy || head.firing));
    if (this._headWasActive && !active) machine.requestSnapshot();
    this._headWasActive = active;
  }

  _acceptSnapshot(page) {
    if (!this.hasContent) return true;
    if (this._shownIndex !== SNAPSHOT_INDEX) return true; // new epoch after a bake
    return page.fires >= this._shownFires;
  }

  // Content must always land ON paper. If a page's top (its blank margin, or an
  // unexpected reverse-feed) pokes above the current leading edge, extend the
  // paper body upward so the edge covers it.
  _extendPaperTop(topLocal) {
    if (topLocal < this._paperTopLocal) {
      this._paperTopLocal = topLocal;
      this._paperBody.style.top = topLocal + 'px';
    }
  }

  // ---- rasterise a 1bpp payload into `canvas` (ink on TRANSPARENT paper) -----
  // Ink pixels are opaque INK; paper pixels are fully transparent (alpha 0) so the
  // white paper body shows through and, when a page is composited over an already-
  // baked page at overlapping absolute-LF coordinates, only ink is written —
  // the two layers OR together (overprint / ink merge) instead of the newer
  // page's paper erasing the older page's dots.
  _renderPayload(page, canvas, ctx) {
    const { width, height, stride, bytes } = page;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      ctx.imageSmoothingEnabled = false;
    }
    const img = ctx.createImageData(width, height);
    const pxd = img.data;
    for (let y = 0; y < height; y++) {
      const rowByte = y * stride;
      const outRow = y * width * 4;
      for (let bx = 0; bx < stride; bx++) {
        const v = bytes[rowByte + bx];
        const x0 = bx << 3;
        for (let bit = 0; bit < 8; bit++) {
          const x = x0 + bit;
          if (x >= width) break;
          const on = (v >> (7 - bit)) & 1;
          const o = outRow + x * 4;
          if (on) {
            pxd[o] = INK[0]; pxd[o + 1] = INK[1]; pxd[o + 2] = INK[2];
            pxd[o + 3] = 255;               // ink: opaque
          } else {
            pxd[o + 3] = 0;                  // paper: transparent (body shows through)
          }
        }
      }
    }
    // putImageData overwrites the canvas region wholesale (it ignores compositing),
    // which is correct for the scratch/live canvases (each holds ONE page). The
    // UNION across pages happens when the scratch is drawImage'd onto a tile in
    // _bakePage (source-over: transparent paper leaves prior ink intact).
    ctx.putImageData(img, 0, 0);
  }

  // Local placement of a page in the roll. The bitmap carries a MARGIN-px border
  // (px, 1/180" both axes); its content origin is raw carriage step xMin (frame
  // x=0 == PAPER_LEFT) and raw LF yMin (roll y=0 == tearBase). We convert the raw
  // anchor to native px with PX_PER_STEP / PX_PER_LF, then back off the px MARGIN.
  _pageLeftLocal(page) {
    return (page.xMin - PAPER_LEFT_STEPS) * PX_PER_STEP - BMP_MARGIN;
  }
  _pageTopLocal(page) {
    return (page.yMin - this._tearBaseLF) * PX_PER_LF - BMP_MARGIN;
  }

  // Composite a flushed page onto the roll tiles at its absolute LF offset.
  _bakePage(page) {
    this._renderPayload(page, this._scratch, this._scratchCtx);
    const w = page.width;
    const h = page.height;
    const leftLocal = this._pageLeftLocal(page);
    const topLocal = this._pageTopLocal(page);
    this._extendPaperTop(topLocal);
    const t0 = Math.floor(topLocal / TILE_H);
    const t1 = Math.floor((topLocal + h - 1) / TILE_H);
    for (let t = t0; t <= t1; t++) {
      const tile = this._getTile(t);
      // drawImage clips to the tile box, so a page straddling tiles paints each.
      tile.ctx.drawImage(this._scratch, 0, 0, w, h,
        leftLocal, topLocal - t * TILE_H, w, h);
    }
    this._contentTopLocal = this._contentTopLocal === null
      ? topLocal : Math.min(this._contentTopLocal, topLocal);
    this._contentBottomLocal = Math.max(this._contentBottomLocal, topLocal + h);
    this._enforceRetention();
  }

  _getTile(idx) {
    let tile = this._tiles.get(idx);
    if (!tile) {
      const canvas = document.createElement('canvas');
      canvas.width = PAPER_WIDTH_STEPS;
      canvas.height = TILE_H;
      canvas.className = 'paper-tile';
      canvas.style.position = 'absolute';
      canvas.style.left = '0';
      canvas.style.top = (idx * TILE_H) + 'px';
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      // Insert before the live overlay so tiles stay beneath the in-progress page.
      this.surface.insertBefore(canvas, this._liveCanvas);
      tile = { canvas, ctx };
      this._tiles.set(idx, tile);
    }
    return tile;
  }

  // Bound memory: drop tiles that fall wholly above the retention window.
  _enforceRetention() {
    const cutoff = this._contentBottomLocal - MAX_ROLL_ROWS;
    if (cutoff <= 0) return;
    let dropped = 0;
    for (const [idx, tile] of this._tiles) {
      if ((idx + 1) * TILE_H <= cutoff) {
        tile.canvas.remove();
        this._tiles.delete(idx);
        this._droppedBelowLocal = Math.max(this._droppedBelowLocal, (idx + 1) * TILE_H);
        dropped++;
      }
    }
    if (dropped) {
      console.warn(`[ms2401] printer roll exceeded ${MAX_ROLL_ROWS} LF rows; ` +
        `dropped ${dropped} oldest tile(s) (${dropped * TILE_H} rows)`);
    }
  }

  // Show the in-progress page as an overlay above the baked tiles.
  _showLive(page) {
    this._renderPayload(page, this._liveCanvas, this._liveCtx);
    this._liveLeftLocal = this._pageLeftLocal(page);
    this._liveTopLocal = this._pageTopLocal(page);
    this._extendPaperTop(this._liveTopLocal);
    this._liveBottomLocal = this._liveTopLocal + page.height;
    this._liveFires = page.fires;
    this._liveCanvas.style.left = this._liveLeftLocal + 'px';
    this._liveCanvas.style.top = this._liveTopLocal + 'px';
    this._liveCanvas.style.display = 'block';
    this._liveVisible = true;
  }

  _hideLive() {
    this._liveCanvas.style.display = 'none';
    this._liveVisible = false;
  }

  _markContent() {
    this.hasContent = true;
    this._freshPaper = false;
  }

  // ---- vertical anchor: pin the current print row to the print line ---------
  _updateAnchor(head) {
    if (head && head.valid) this._currentLF = head.y;
  }

  // translateY (native) that seats the paper's current feed position on the
  // print line / slot: the leading edge (local 0) rides (fed·PX_PER_LF) above
  // the slot, so with nothing fed NO paper is visible.
  _followTy() {
    const cur = this._currentLF === null ? this._tearBaseLF : this._currentLF;
    const fedPx = (cur - this._tearBaseLF) * PX_PER_LF;
    return this._printLineNativeY - fedPx;
  }

  // The most-scrolled-back translateY: the OLDEST reachable roll row seated on the
  // print line (so the top of the strip / tear line comes into view with the
  // blank leader above it). Bounds the browse range at the top.
  _topBoundTy() {
    const oldest = Math.max(
      this._contentTopLocal === null ? 0 : this._contentTopLocal,
      this._droppedBelowLocal,
      this._liveVisible ? this._liveTopLocal : 0);
    return this._printLineNativeY - oldest;
  }

  _applyTransform() {
    const followTy = this._followTy();
    let ty;
    if (this._follow) {
      ty = followTy;
      this._browseTy = ty;
    } else {
      // Browse: clamp between the live print line (bottom = followTy) and the
      // oldest reachable roll row on the print line (top = topBoundTy). If the
      // whole strip already fits above the print line, there is nothing to
      // browse — snap back to follow.
      const hi = this._topBoundTy();
      if (hi <= followTy + 1) {
        this._follow = true;
        this._showFollow(false);
        ty = followTy;
        this._browseTy = ty;
      } else {
        if (this._browseTy < followTy) this._browseTy = followTy;
        if (this._browseTy > hi) this._browseTy = hi;
        ty = this._browseTy;
      }
    }
    this._setTransition(this._follow);
    if (ty !== this._lastTyWritten) {
      this._lastTyWritten = ty;
      this.surface.style.transform = `translateY(${ty}px)`;
      // The tractor drive pins advance WITH the feed (the paper's own holes ride
      // along automatically as children of the paper body). Same half-pitch phase
      // as the holes so pins read as engaged wherever the rail is exposed.
      const pinY = (ty + HOLE_PITCH_PX / 2) * this._scale;
      for (const s of this.sprockets) s.style.backgroundPosition = `50% ${pinY}px`;
    }
  }

  // Animate the surface (and holes) on follow-mode advances; snap while browsing
  // or under prefers-reduced-motion.
  _setTransition(animate) {
    const on = !!animate && !this.reduceMotion;
    if (on === this._animOn) return;
    this._animOn = on;
    this.surface.style.transition = on ? 'transform 0.35s ease-out' : 'none';
    for (const s of this.sprockets) {
      s.style.transition = on ? 'background-position 0.35s ease-out' : 'none';
    }
  }

  _showFollow(show) {
    if (this.followBtn) this.followBtn.classList.toggle('is-hidden', !show);
  }

  _reengage() {
    this._follow = true;
    this._showFollow(false);
    this._applyTransform();
  }

  _startPaperDrag(e) {
    if (e.button !== undefined && e.button !== 0) return;
    const m = this._machine;
    if (!m || !m.powered || !m.ready || this._scale <= 0) return;
    const vr = this.viewport.getBoundingClientRect();
    const printLineY = vr.top + PRINT_LINE_FRAC * vr.height;
    if (e.clientY > printLineY + 2) return;

    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    this._paperDrag = {
      pointerId: e.pointerId,
      target: e.currentTarget,
      lastY: e.clientY,
      startLF: this._currentLF === null ? this._tearBaseLF : this._currentLF,
      residualLF: 0,
    };
    this.viewport.classList.add('is-paper-dragging');
    this._showReseat(true);
    this._follow = true;
    this._showFollow(false);
    this._setTransition(false);
  }

  _movePaperDrag(e) {
    const drag = this._paperDrag;
    if (!drag || e.pointerId !== drag.pointerId) return;
    e.preventDefault();

    const dy = e.clientY - drag.lastY;
    drag.lastY = e.clientY;
    drag.residualLF += (-dy / this._scale) / PX_PER_LF;

    const wholePairs = Math.trunc(drag.residualLF / 2);
    const deltaLF = wholePairs * 2;
    if (!deltaLF) return;
    drag.residualLF -= deltaLF;
    this._rollPaperBy(deltaLF);
  }

  _endPaperDrag(e) {
    const drag = this._paperDrag;
    if (!drag || e.pointerId !== drag.pointerId) return;
    e.preventDefault();
    this._finishPaperDrag();
  }

  _finishPaperDrag() {
    const drag = this._paperDrag;
    if (!drag) return;
    try { drag.target.releasePointerCapture(drag.pointerId); } catch (_) {}
    this._paperDrag = null;
    this.viewport.classList.remove('is-paper-dragging');
    this._showReseat(false);
    this._setTransition(this._follow);
  }

  _showReseat(show) {
    if (this._reseatBtn) this._reseatBtn.classList.toggle('is-hidden', !show);
  }

  _reseatDraggedPaper() {
    const drag = this._paperDrag;
    if (!drag) return;
    this._rollPaperTo(drag.startLF);
    this._finishPaperDrag();
  }

  _rollPaperTo(targetLF) {
    const cur = this._currentLF === null ? this._tearBaseLF : this._currentLF;
    const deltaLF = Math.round((targetLF - cur) / 2) * 2;
    if (deltaLF) this._rollPaperBy(deltaLF);
    else this._applyTransform();
  }

  _rollPaperBy(deltaLF) {
    const m = this._machine;
    if (!m || !m.powered || !m.ready || !deltaLF) return false;
    const nextLF = m.knob(deltaLF | 0);
    if (Number.isFinite(nextLF)) this._currentLF = nextLF;
    if (m.requestSnapshot) m.requestSnapshot();
    this._follow = true;
    this._showFollow(false);
    this._applyTransform();
    return true;
  }

  // Wheel over the paper well scrolls back through the roll. Wheel-up detaches
  // into browse mode; scrolling back down to the print line re-engages follow.
  _onWheel(e) {
    e.preventDefault();
    if (this._scale <= 0 || !this.hasContent) return;
    if (this._follow) {
      this._follow = false;
      this._browseTy = this._followTy();
      this._showFollow(true);
    }
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;                    // lines -> px
    else if (e.deltaMode === 2) dy *= this.viewport.clientHeight; // pages -> px
    // Wheel-up (dy<0) reveals earlier content: paper moves down (ty increases).
    this._browseTy += (-dy) / this._scale;
    if (this._browseTy <= this._followTy() + 2) {       // scrolled back to live
      this._follow = true;
      this._showFollow(false);
    }
    this._applyTransform();
  }

  hasPage() { return this.hasContent; }

  // ---- tear-off: composite the WHOLE strip since the last tear -------------
  // Returns an offscreen canvas (WHITE paper + all baked pages + the live partial
  // at their true relative LF spacing). No sprocket holes — the perforated edges
  // tear off with the machine. No side effects; exportPng()/stripInfo() use it.
  _buildStrip(pad = 24) {
    let top = this._contentTopLocal;
    let bottom = this._contentBottomLocal;
    if (this._liveVisible) {
      top = top === null ? this._liveTopLocal : Math.min(top, this._liveTopLocal);
      bottom = Math.max(bottom, this._liveBottomLocal);
    }
    if (top === null || !(bottom > top)) return null;

    const out = document.createElement('canvas');
    out.width = PAPER_WIDTH_STEPS + pad * 2;
    out.height = Math.round(bottom - top) + pad * 2;
    const octx = out.getContext('2d');
    octx.imageSmoothingEnabled = false;
    octx.fillStyle = `rgb(${PAPER_BG.join(',')})`; // opaque white background
    octx.fillRect(0, 0, out.width, out.height);
    for (const [idx, tile] of this._tiles) {
      octx.drawImage(tile.canvas, pad, pad + (idx * TILE_H - top));
    }
    if (this._liveVisible) {
      octx.drawImage(this._liveCanvas,
        pad + this._liveLeftLocal, pad + (this._liveTopLocal - top));
    }
    return out;
  }

  // Programmatic verification aid: the strip's dimensions + ink-dot count.
  stripInfo() {
    const out = this._buildStrip(0);
    if (!out) return { width: 0, height: 0, dots: 0 };
    const ctx = out.getContext('2d');
    const data = ctx.getImageData(0, 0, out.width, out.height).data;
    let dots = 0;
    const thresh = (PAPER_BG[0] + INK[0]) >> 1; // ink is far darker than paper
    for (let i = 0; i < data.length; i += 4) if (data[i] < thresh) dots++;
    return { width: out.width, height: out.height, dots };
  }

  renderPreview(canvas) {
    const out = this._buildStrip(12);
    if (!out || !canvas) {
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
      return false;
    }
    canvas.width = out.width;
    canvas.height = out.height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(out, 0, 0);
    return true;
  }

  // Diagnostic snapshot of roll, anchor, and scrollback state.
  debugState() {
    const cur = this._currentLF === null ? this._tearBaseLF : this._currentLF;
    const fedLF = cur - this._tearBaseLF;
    return {
      hasContent: this.hasContent,
      tiles: this._tiles.size,
      tearBaseLF: this._tearBaseLF,
      currentLF: this._currentLF,
      fedLF,
      // Visible paper height above the print line, in SCREEN px (the no-paper
      // invariant: 0 when nothing has fed since the tear base).
      paperAbovePx: Math.max(0, (fedLF * PX_PER_LF - this._paperTopLocal) * this._scale),
      paperTopLocal: this._paperTopLocal,
      contentTopLocal: this._contentTopLocal,
      contentBottomLocal: this._contentBottomLocal,
      stripRows: this._contentTopLocal === null
        ? 0 : Math.round(this._contentBottomLocal - this._contentTopLocal),
      liveVisible: this._liveVisible,
      liveFires: this._liveVisible ? this._liveFires : 0,
      liveTopLocal: this._liveTopLocal,
      liveBottomLocal: this._liveBottomLocal,
      maxRealIndex: this._maxRealIndex,
      scale: this._scale,
      follow: this._follow,
      browseTy: this._browseTy,
      followTy: this._followTy(),
    };
  }

  // 撕下 TEAR OFF: export the entire strip, then physically tear the paper.
  exportPng() {
    const out = this._buildStrip(24);
    if (!out) return false;
    out.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `ms2401-strip-${ts}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
    this._tear();
    return true;
  }

  // Physically tear: remove the strip from the platen, rebase the leading edge
  // to the current feed position (fed-since-tear returns to 0, so NO paper shows
  // until the next feed), and re-engage follow.
  _tear() {
    this._tearBaseLF = this._currentLF === null ? this._tearBaseLF : this._currentLF;
    // Rebase the knob's clamp floor in C to the new tear line: after tearing, the
    // paper can no longer be rolled back above where it was torn (analog paper,
    // no BIOS involvement — see vm86_printer_set_tear_base).
    if (this._machine && this._machine.setTearBase) {
      this._machine.setTearBase(this._tearBaseLF);
    }
    for (const [, tile] of this._tiles) tile.canvas.remove();
    this._tiles.clear();
    this._hideLive();
    this._contentTopLocal = null;
    this._contentBottomLocal = 0;
    this._droppedBelowLocal = 0;
    this._paperTopLocal = 0;
    this._paperBody.style.top = '0px';
    this.hasContent = false;
    this._shownIndex = null;
    this._shownFires = -1;
    this._follow = true;
    this._browseTy = 0;
    this._lastTyWritten = null;
    this._showFollow(false);
    this._freshPaper = true;   // keep 新纸 in the status until the next content
    this.statusEl.textContent = '新纸';
    this._applyTransform();
  }

  // ---- horizontal head placement + firing state ----------------------------
  _updateHead(head) {
    if (!head || !head.valid) return;
    // head.x is already relative to PAPER_LEFT (LESB ref); map against the SAME
    // constant printable width the page bitmap uses so the head rides the dots.
    const frac = Math.min(1, Math.max(0, head.x / PAPER_WIDTH_STEPS));

    // Place the head CENTRE at that fraction of the on-screen content box.
    // Measuring the scaled content rect vs the carriage-overlay rect keeps the
    // head aligned with the dot columns regardless of CSS insets / scale.
    const paperRect = this.scaler.getBoundingClientRect();
    const overlayRect = this.overlay.getBoundingClientRect();
    let pxPos;
    if (paperRect.width > 0 && overlayRect.width > 0) {
      const centerX = (paperRect.left - overlayRect.left) + frac * paperRect.width;
      pxPos = centerX - this.head.offsetWidth / 2;
    } else {
      pxPos = frac * Math.max(0, this.overlay.clientWidth - this.head.offsetWidth);
    }
    this.head.style.transform = `translateX(${pxPos}px)`;

    const active = head.firing || head.busy;
    this.head.classList.toggle('is-firing', !!head.firing);
    this.head.classList.toggle('is-busy', !!head.busy && !head.firing);
    this.overlay.classList.toggle('is-active', !!active);

    const idx = this._shownIndex === null ? (this._freshPaper ? '新纸' : '待机')
      : (this._shownIndex === SNAPSHOT_INDEX ? '当前' : `第${this._shownIndex}页`);
    const browse = this._follow ? '' : '浏览 · ';
    this.statusEl.textContent =
      `${browse}LF ${this._currentLF | 0} · ` +
      `${this._liveVisible ? this._liveFires + '点 · ' : ''}${idx}`;
  }
}
